<template>
	<transition name="cookie-fade">
		<div
			v-if="isVisible"
			class="cookie-modal"
			role="dialog"
			aria-modal="true"
			:aria-labelledby="
				manageIsVisible ? 'cookie-settings-title' : 'cookie-notice-title'
			"
			@click.self="declineCookies"
		>
			<div class="cookie-modal__overlay"></div>

			<div class="cookie-modal__window">
				<button
					type="button"
					class="cookie-modal__close"
					:aria-label="$t('Закрыть')"
					@click="declineCookies"
				>
					<svg
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M4 4L14 14"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
						/>

						<path
							d="M14 4L4 14"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
						/>
					</svg>
				</button>

				<div class="cookie-modal__icon">
					<svg
						viewBox="0 0 48 48"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M40 25C31.7 25 25 18.3 25 10C25 8.6 25.2 7.3 25.5 6C15.5 6.3 7.5 14.5 7.5 24.6C7.5 34.9 15.8 43.2 26.1 43.2C36.2 43.2 44.4 35.2 44.7 25.2C43.2 25.1 41.7 25 40 25Z"
							stroke="currentColor"
							stroke-width="3"
							stroke-linejoin="round"
						/>

						<circle cx="18" cy="22" r="2.5" fill="currentColor" />

						<circle cx="28" cy="33" r="2.5" fill="currentColor" />

						<circle cx="17" cy="33" r="1.8" fill="currentColor" />
					</svg>
				</div>

				<template v-if="!manageIsVisible">
					<div class="cookie-modal__header">
						<p class="cookie-modal__eyebrow">
							{{ $t('КОНФИДЕНЦИАЛЬНОСТЬ') }}
						</p>

						<h2 id="cookie-notice-title" class="cookie-modal__title">
							{{ $t('Мы используем файлы cookie') }}
						</h2>
					</div>

					<p class="cookie-modal__description">
						{{
							$t(
								'Мы используем файлы cookie для корректной работы сайта и улучшения вашего пользовательского опыта.',
							)
						}}
					</p>

					<p class="cookie-modal__description">
						{{
							$t(
								'Необходимые cookie всегда активны, поскольку без них основные функции сайта не смогут работать.',
							)
						}}
					</p>

					<p class="cookie-modal__description">
						{{
							$t(
								'Дополнительные cookie вы можете настроить самостоятельно.',
							)
						}}

						<template v-if="cookiePage">
							{{ $t('Подробнее читайте на странице') }}

							<button
								type="button"
								class="cookie-modal__link"
								@click="openCookiePage"
							>
								{{ cookiePage.title }}</button
							>.
						</template>
					</p>

					<div class="cookie-modal__actions">
						<button
							type="button"
							class="button shadowed cookie-modal__accept"
							@click="acceptAllCookies"
						>
							<span>{{ $t('ПРИНЯТЬ ВСЕ') }}</span>
						</button>

						<button
							type="button"
							class="button bordered cookie-modal__manage"
							@click="manageCookies"
						>
							<span>{{ $t('НАСТРОИТЬ') }}</span>
						</button>

						<button
							type="button"
							class="cookie-modal__decline"
							@click="declineCookies"
						>
							{{ $t('Отклонить необязательные') }}
						</button>
					</div>
				</template>

				<template v-else>
					<div class="cookie-modal__header">
						<p class="cookie-modal__eyebrow">
							{{ $t('НАСТРОЙКИ COOKIE') }}
						</p>

						<h2 id="cookie-settings-title" class="cookie-modal__title">
							{{ $t('Управление файлами cookie') }}
						</h2>

						<p class="cookie-modal__subtitle">
							{{
								$t(
									'Выберите категории cookie, использование которых вы разрешаете.',
								)
							}}
						</p>
					</div>

					<div class="cookie-settings">
						<label class="cookie-option cookie-option_required">
							<div class="cookie-option__content">
								<div class="cookie-option__heading">
									<div>
										<h3 class="cookie-option__title">
											{{ $t('Необходимые cookie') }}
										</h3>

										<span class="cookie-option__badge">
											{{ $t('ВСЕГДА АКТИВНЫ') }}
										</span>
									</div>

									<span class="cookie-switch cookie-switch_disabled">
										<input type="checkbox" checked disabled />

										<span class="cookie-switch__track">
											<span class="cookie-switch__thumb"></span>
										</span>
									</span>
								</div>

								<p class="cookie-option__description">
									{{
										$t(
											'Эти cookie необходимы для авторизации, работы корзины, безопасности и основных функций сайта.',
										)
									}}
								</p>
							</div>
						</label>

						<label class="cookie-option">
							<div class="cookie-option__content">
								<div class="cookie-option__heading">
									<h3 class="cookie-option__title">
										{{ $t('Аналитические cookie') }}
									</h3>

									<span class="cookie-switch">
										<input v-model="analytics" type="checkbox" />

										<span class="cookie-switch__track">
											<span class="cookie-switch__thumb"></span>
										</span>
									</span>
								</div>

								<p class="cookie-option__description">
									{{
										$t(
											'Помогают нам понять, как посетители используют сайт, чтобы мы могли улучшать его работу.',
										)
									}}
								</p>
							</div>
						</label>

						<label class="cookie-option">
							<div class="cookie-option__content">
								<div class="cookie-option__heading">
									<h3 class="cookie-option__title">
										{{ $t('Рекламные cookie') }}
									</h3>

									<span class="cookie-switch">
										<input v-model="advertising" type="checkbox" />

										<span class="cookie-switch__track">
											<span class="cookie-switch__thumb"></span>
										</span>
									</span>
								</div>

								<p class="cookie-option__description">
									{{
										$t(
											'Используются для показа более подходящих рекламных сообщений и оценки их эффективности.',
										)
									}}
								</p>
							</div>
						</label>

						<label class="cookie-option">
							<div class="cookie-option__content">
								<div class="cookie-option__heading">
									<h3 class="cookie-option__title">
										{{ $t('Функциональные cookie') }}
									</h3>

									<span class="cookie-switch">
										<input v-model="functional" type="checkbox" />

										<span class="cookie-switch__track">
											<span class="cookie-switch__thumb"></span>
										</span>
									</span>
								</div>

								<p class="cookie-option__description">
									{{
										$t(
											'Запоминают выбранные настройки и предоставляют дополнительные возможности сайта.',
										)
									}}
								</p>
							</div>
						</label>
					</div>

					<div
						class="cookie-modal__actions cookie-modal__actions_settings"
					>
						<button
							type="button"
							class="button shadowed cookie-modal__save"
							@click="acceptSelectedCookies"
						>
							<span>{{ $t('СОХРАНИТЬ ВЫБОР') }}</span>
						</button>

						<button
							type="button"
							class="button bordered cookie-modal__all"
							@click="acceptAllCookies"
						>
							<span>{{ $t('ПРИНЯТЬ ВСЕ') }}</span>
						</button>

						<button
							type="button"
							class="cookie-modal__back"
							@click="manageIsVisible = false"
						>
							{{ $t('Вернуться назад') }}
						</button>
					</div>
				</template>
			</div>
		</div>
	</transition>
</template>

<script>
import VueCookies from 'vue-js-cookie';

export default {
	name: 'CookieModal',

	props: {
		textPageList: {
			type: Array,
			default: () => [],
		},
	},

	data() {
		return {
			isVisible: !VueCookies.get('cookiesConsent'),
			manageIsVisible: false,

			analytics: false,
			advertising: false,
			functional: false,
		};
	},

	computed: {
		cookiePage() {
			return (
				this.textPageList.find((page) => {
					const title = String(page.title || '').toLowerCase();

					return (
						title.includes('cookie') ||
						title.includes('cookies') ||
						title.includes('куки') ||
						title.includes('cookie-файл')
					);
				}) || null
			);
		},
	},

	mounted() {
		this.loadSavedPreferences();
		this.updateBodyScroll();
	},

	beforeUnmount() {
		document.body.classList.remove('hidden');
	},

	methods: {
		loadSavedPreferences() {
			const savedPreferences = VueCookies.get('cookiesPreferences');

			if (!savedPreferences) {
				return;
			}

			try {
				const preferences =
					typeof savedPreferences === 'string'
						? JSON.parse(savedPreferences)
						: savedPreferences;

				this.analytics = Boolean(preferences.analytics);
				this.advertising = Boolean(preferences.advertising);
				this.functional = Boolean(preferences.functional);
			} catch (error) {
				this.analytics = false;
				this.advertising = false;
				this.functional = false;
			}
		},

		acceptAllCookies() {
			this.analytics = true;
			this.advertising = true;
			this.functional = true;

			this.saveConsent('accepted');
		},

		acceptSelectedCookies() {
			this.saveConsent('selected');
		},

		declineCookies() {
			this.analytics = false;
			this.advertising = false;
			this.functional = false;

			this.saveConsent('declined');
		},

		saveConsent(status) {
			const preferences = {
				necessary: true,
				analytics: this.analytics,
				advertising: this.advertising,
				functional: this.functional,
			};

			VueCookies.set('cookiesConsent', status, 365);
			VueCookies.set('cookiesPreferences', JSON.stringify(preferences), 365);

			this.isVisible = false;
			this.manageIsVisible = false;
			this.updateBodyScroll();

			this.$emit('cookiesUpdated', preferences);
		},

		manageCookies() {
			this.manageIsVisible = true;
		},

		openCookiePage() {
			if (!this.cookiePage) {
				return;
			}

			this.isVisible = false;
			this.updateBodyScroll();

			if (this.$parent && typeof this.$parent.goToPage === 'function') {
				this.$parent.goToPage(this.cookiePage);
			}
		},

		updateBodyScroll() {
			if (this.isVisible) {
				document.body.classList.add('hidden');
				return;
			}

			document.body.classList.remove('hidden');
		},
	},
};
</script>

<style scoped lang="scss">
.cookie-modal {
	position: fixed;
	inset: 0;
	z-index: 1000;

	display: flex;
	align-items: center;
	justify-content: center;

	padding: 24px;
}

.cookie-modal__overlay {
	position: absolute;
	inset: 0;

	background: rgba(7, 9, 12, 0.78);
	backdrop-filter: blur(6px);
}

.cookie-modal__window {
	position: relative;
	z-index: 1;

	width: 100%;
	max-width: 620px;
	max-height: calc(100vh - 48px);

	padding: 30px;

	background: var(--bunker-86);
	border: 1px solid var(--outer-space);
	border-radius: 12px;

	overflow-y: auto;

	box-shadow: 8px 8px 0 #0c0f13, inset 3px 3px 0 rgba(255, 255, 255, 0.025),
		inset -3px -3px 0 rgba(0, 0, 0, 0.22);

	scrollbar-width: thin;
	scrollbar-color: var(--outer-space) transparent;
}

.cookie-modal__close {
	position: absolute;
	top: 16px;
	right: 16px;

	width: 36px;
	height: 36px;
	display: flex;
	align-items: center;
	justify-content: center;

	padding: 0;

	color: var(--pewter);
	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 7px;

	transition: color 0.2s ease, background-color 0.2s ease,
		border-color 0.2s ease, transform 0.2s ease;

	svg {
		width: 16px;
		height: 16px;
	}

	@media (any-hover: hover) {
		&:hover {
			color: var(--gray-nurse);
			background: rgba(255, 71, 102, 0.12);
			border-color: rgba(255, 71, 102, 0.4);
			transform: translateY(-1px);
		}
	}
}

.cookie-modal__icon {
	width: 68px;
	height: 68px;
	display: flex;
	align-items: center;
	justify-content: center;

	margin: 0 auto 20px;

	color: var(--44);
	background: var(--heavy-metal);
	border: 1px solid var(--outer-space);
	border-radius: 10px;

	box-shadow: inset 3px 3px 0 rgba(255, 255, 255, 0.03),
		inset -3px -3px 0 rgba(0, 0, 0, 0.25);

	svg {
		width: 40px;
		height: 40px;
	}
}

.cookie-modal__header {
	padding-right: 26px;
	text-align: center;
}

.cookie-modal__eyebrow {
	margin: 0 0 8px;

	font-family: var(--font-3);
	font-size: 9px;
	font-weight: 700;
	line-height: 14px;
	letter-spacing: 0.08em;

	color: var(--44);
}

.cookie-modal__title {
	margin: 0;

	font-family: var(--font-2);
	font-size: 27px;
	font-weight: 700;
	line-height: 35px;

	color: var(--gray-nurse);
}

.cookie-modal__subtitle {
	max-width: 470px;
	margin: 10px auto 0;

	font-family: var(--font-1);
	font-size: 14px;
	line-height: 21px;

	color: var(--pewter);
}

.cookie-modal__description {
	max-width: 540px;
	margin: 16px auto 0;

	font-family: var(--font-1);
	font-size: 14px;
	font-weight: 400;
	line-height: 21px;
	text-align: center;

	color: var(--pewter);
}

.cookie-modal__link {
	display: inline;
	margin: 0;
	padding: 0;

	font: inherit;
	font-weight: 600;
	line-height: inherit;

	color: var(--44);
	background: transparent;
	border: 0;

	@media (any-hover: hover) {
		&:hover {
			color: var(--gray-nurse);
			text-decoration: underline;
		}
	}
}

/* Cookie settings */

.cookie-settings {
	display: flex;
	flex-direction: column;
	gap: 12px;

	margin-top: 24px;
}

.cookie-option {
	display: block;
	margin: 0;
	padding: 17px;

	background: var(--white-woodsmoke);
	border: 1px solid var(--outer-space);
	border-radius: 9px;

	cursor: pointer;

	transition: background-color 0.2s ease, border-color 0.2s ease;

	@media (any-hover: hover) {
		&:hover {
			background: var(--heavy-metal);
			border-color: rgba(33, 193, 126, 0.3);
		}
	}

	&_required {
		cursor: default;
	}
}

.cookie-option__heading {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 18px;
}

.cookie-option__title {
	margin: 0;

	font-family: var(--font-2);
	font-size: 15px;
	font-weight: 600;
	line-height: 21px;

	color: var(--gray-nurse);
}

.cookie-option__badge {
	display: inline-flex;
	margin-top: 6px;
	padding: 4px 7px;

	font-family: var(--font-3);
	font-size: 7px;
	font-weight: 700;
	line-height: 10px;
	letter-spacing: 0.04em;

	color: var(--44);
	background: rgba(33, 193, 126, 0.08);
	border: 1px solid rgba(33, 193, 126, 0.22);
	border-radius: 5px;
}

.cookie-option__description {
	max-width: 470px;
	margin: 8px 0 0;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 400;
	line-height: 19px;

	color: var(--pewter);
}

/* Custom switch */

.cookie-switch {
	position: relative;
	flex: 0 0 auto;
	display: block;

	input {
		position: absolute;
		width: 1px;
		height: 1px;
		opacity: 0;
		pointer-events: none;
	}

	input:checked + .cookie-switch__track {
		background: var(--31);
		border-color: var(--44);

		.cookie-switch__thumb {
			transform: translateX(18px);
			background: var(--gray-nurse);
		}
	}

	input:focus-visible + .cookie-switch__track {
		box-shadow: 0 0 0 3px rgba(33, 193, 126, 0.15);
	}
}

.cookie-switch__track {
	width: 42px;
	height: 24px;
	display: flex;
	align-items: center;
	padding: 3px;

	background: var(--bunker);
	border: 1px solid var(--outer-space);
	border-radius: 999px;

	box-shadow: inset 2px 2px 0 rgba(0, 0, 0, 0.3);

	transition: background-color 0.2s ease, border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.cookie-switch__thumb {
	width: 16px;
	height: 16px;

	background: var(--pewter);
	border-radius: 50%;

	box-shadow: 1px 1px 0 rgba(0, 0, 0, 0.35);

	transition: transform 0.2s ease, background-color 0.2s ease;
}

.cookie-switch_disabled {
	opacity: 0.7;
}

/* Actions */

.cookie-modal__actions {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-wrap: wrap;
	gap: 12px;

	margin-top: 28px;
}

.cookie-modal__accept,
.cookie-modal__manage,
.cookie-modal__save,
.cookie-modal__all {
	min-height: 42px;
	justify-content: center;
	margin: 0;
	padding: 12px 20px;

	font-family: var(--font-3);
	font-size: 9px;
	font-weight: 700;
	line-height: 13px;
}

.cookie-modal__accept {
	min-width: 160px;
}

.cookie-modal__manage {
	min-width: 130px;
}

.cookie-modal__save {
	min-width: 184px;
}

.cookie-modal__all {
	min-width: 145px;
}

.cookie-modal__decline,
.cookie-modal__back {
	width: 100%;
	margin-top: 4px;
	padding: 4px;

	font-family: var(--font-1);
	font-size: 12px;
	font-weight: 500;
	line-height: 18px;

	color: var(--pewter);
	background: transparent;
	border: 0;

	@media (any-hover: hover) {
		&:hover {
			color: var(--gray-nurse);
			text-decoration: underline;
		}
	}
}

/* Transition */

.cookie-fade-enter-active,
.cookie-fade-leave-active {
	transition: opacity 0.25s ease;

	.cookie-modal__window {
		transition: opacity 0.25s ease, transform 0.25s ease;
	}
}

.cookie-fade-enter-from,
.cookie-fade-leave-to {
	opacity: 0;

	.cookie-modal__window {
		opacity: 0;
		transform: translateY(12px) scale(0.98);
	}
}

/* Responsive */

@media (max-width: 767.98px) {
	.cookie-modal {
		align-items: flex-end;
		padding: 12px;
	}

	.cookie-modal__window {
		max-height: calc(100vh - 24px);
		padding: 24px 18px 20px;
		border-radius: 11px;
	}

	.cookie-modal__icon {
		width: 58px;
		height: 58px;
		margin-bottom: 17px;

		svg {
			width: 34px;
			height: 34px;
		}
	}

	.cookie-modal__title {
		font-size: 23px;
		line-height: 30px;
	}

	.cookie-modal__description,
	.cookie-modal__subtitle {
		font-size: 13px;
		line-height: 20px;
	}

	.cookie-option {
		padding: 15px;
	}

	.cookie-modal__actions {
		align-items: stretch;
		flex-direction: column;
	}

	.cookie-modal__accept,
	.cookie-modal__manage,
	.cookie-modal__save,
	.cookie-modal__all {
		width: 100%;
	}
}

@media (max-width: 420px) {
	.cookie-modal {
		padding: 0;
	}

	.cookie-modal__window {
		max-height: 100vh;
		border-right: 0;
		border-bottom: 0;
		border-left: 0;
		border-radius: 12px 12px 0 0;
	}

	.cookie-option__heading {
		gap: 12px;
	}
}
</style>
